/**
 * Лента «Вакансии»: фильтр пользователя, бесплатный отбор по словам, проверка уровня/формата/зарплаты
 * и инструмент Claude для разбора постов из Telegram-каналов.
 */
import type { DateStr } from './dates';
import { FORMATS, normName, type Format } from './model';

export const FEED_LEVELS = ['junior', 'middle', 'senior', 'lead'] as const;
export type FeedLevel = (typeof FEED_LEVELS)[number];
export const FEED_LEVEL_LABELS: Record<FeedLevel, string> = {
  junior: 'Junior',
  middle: 'Middle',
  senior: 'Senior',
  lead: 'Lead',
};

/** Языки объявления, которые можно выбрать в фильтре. */
export const FEED_LANGS = ['ru', 'uk', 'en', 'es'] as const;
export type FeedLang = (typeof FEED_LANGS)[number];
export const FEED_LANG_LABELS: Record<FeedLang, string> = {
  ru: 'Русский',
  uk: 'Украинский',
  en: 'Английский',
  es: 'Испанский',
};

export interface FeedFilter {
  /** Хотя бы одно слово должно встретиться в тексте (без учёта регистра, целым словом). */
  keywords: string[];
  /** Ни одного из этих слов. */
  exclude: string[];
  /** Пусто — любой уровень; неизвестный уровень проходит. */
  levels: FeedLevel[];
  /** Пусто — любой формат; неизвестный формат проходит. */
  formats: Format[];
  /** Минимальная зарплата, $ в месяц: проходит, если верхняя граница вилки не ниже. */
  salaryMinUsd: number | null;
  /** Пропускать вакансии без зарплаты (в Telegram её часто не пишут). */
  includeNoSalary: boolean;
  /** Сохранённый фильтр Talanto для ленты; null — первый из сохранённых. */
  talantoFilterId: string | null;
  /** Брать вакансии с Himalayas (публичный API удалённых вакансий). */
  himalayas: boolean;
  /** Страна, откуда можно работать (ISO-2), плюс «из любой страны»; пусто — только «из любой страны». */
  himalayasCountry: string;
  /** Публичный поиск LinkedIn (без входа в аккаунт). */
  linkedin: boolean;
  /** Где искать на LinkedIn: страна или город, как в поиске LinkedIn. */
  linkedinLocation: string;
  /** Письма LinkedIn «Оповещения о вакансиях» из почты пользователя. */
  linkedinAlerts: boolean;
  /** Djinni (RSS, рынок Украины и Европы): QA с полной удалёнкой. */
  djinni: boolean;
  /** Язык объявления; пусто — любой. Язык, который не удалось определить (короткий текст), проходит. */
  languages: FeedLang[];
}

/** По умолчанию — как фильтр «QA» в Talanto: QA в названии, удалёнка, Middle–Lead, от $3000. */
export const DEFAULT_FEED_FILTER: FeedFilter = {
  keywords: [
    'QA',
    'AQA',
    'SDET',
    'Quality Assurance',
    'тестировщик',
    'тестирование',
    'тест-инженер',
    'Test Automation',
    'Automation Engineer',
    'Tester',
  ],
  exclude: ['Junior', 'Стажёр', 'Стажер', 'Intern', 'Trainee', 'Junior+'],
  levels: ['middle', 'senior', 'lead'],
  formats: ['remote'],
  salaryMinUsd: 3000,
  includeNoSalary: true,
  talantoFilterId: null,
  himalayas: true,
  himalayasCountry: 'ES',
  linkedin: true,
  linkedinLocation: 'Spain',
  linkedinAlerts: true,
  djinni: true,
  languages: [],
};

const words = (v: unknown, def: string[]) =>
  Array.isArray(v) ? [...new Set(v.map((x) => String(x ?? '').trim()).filter(Boolean))].slice(0, 60) : def;

/** Сохранённый фильтр (может быть пустым или старым) → полный, с умолчаниями. */
export function normFeedFilter(raw: unknown): FeedFilter {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof FeedFilter, unknown>>;
  const d = DEFAULT_FEED_FILTER;
  return {
    keywords: words(r.keywords, d.keywords),
    exclude: words(r.exclude, d.exclude),
    levels: Array.isArray(r.levels)
      ? (r.levels.filter((l) => (FEED_LEVELS as readonly unknown[]).includes(l)) as FeedLevel[])
      : d.levels,
    formats: Array.isArray(r.formats)
      ? (r.formats.filter((f) => (FORMATS as readonly unknown[]).includes(f)) as Format[])
      : d.formats,
    salaryMinUsd:
      r.salaryMinUsd === null
        ? null
        : typeof r.salaryMinUsd === 'number' && r.salaryMinUsd >= 0
          ? Math.round(r.salaryMinUsd)
          : d.salaryMinUsd,
    includeNoSalary: typeof r.includeNoSalary === 'boolean' ? r.includeNoSalary : d.includeNoSalary,
    talantoFilterId: typeof r.talantoFilterId === 'string' && r.talantoFilterId ? r.talantoFilterId : null,
    himalayas: typeof r.himalayas === 'boolean' ? r.himalayas : d.himalayas,
    himalayasCountry:
      typeof r.himalayasCountry === 'string' && /^([A-Za-z]{2})?$/.test(r.himalayasCountry)
        ? r.himalayasCountry.toUpperCase()
        : d.himalayasCountry,
    linkedin: typeof r.linkedin === 'boolean' ? r.linkedin : d.linkedin,
    linkedinLocation:
      typeof r.linkedinLocation === 'string' && r.linkedinLocation.trim()
        ? r.linkedinLocation.trim().slice(0, 100)
        : d.linkedinLocation,
    linkedinAlerts: typeof r.linkedinAlerts === 'boolean' ? r.linkedinAlerts : d.linkedinAlerts,
    djinni: typeof r.djinni === 'boolean' ? r.djinni : d.djinni,
    languages: Array.isArray(r.languages)
      ? [...new Set(r.languages.filter((l) => (FEED_LANGS as readonly unknown[]).includes(l)) as FeedLang[])]
      : d.languages,
  };
}

/* ---------- Himalayas ---------- */

/** Поисковые запросы Himalayas из слов фильтра: латиница (сайт англоязычный), не больше 4. */
export function himalayasQueries(f: Pick<FeedFilter, 'keywords'>): string[] {
  return [...new Set(f.keywords.filter((k) => /^[ -~]+$/.test(k)).map((k) => k.trim()))].slice(0, 4);
}

const HIM_SENIORITY: Record<FeedLevel, string[]> = {
  junior: ['Entry-level'],
  middle: ['Mid-level'],
  senior: ['Senior'],
  lead: ['Manager', 'Director'],
};
/** Уровни фильтра → seniority Himalayas («Mid-level,Senior,Manager»). */
export function himalayasSeniority(levels: readonly FeedLevel[]): string {
  return [...new Set(levels.flatMap((l) => HIM_SENIORITY[l]))].join(',');
}
/** Seniority Himalayas → уровень ленты (самый высокий из указанных). */
export function himalayasLevel(seniority: readonly string[] | null | undefined): FeedLevel | null {
  const s = seniority ?? [];
  if (s.some((x) => /manager|director|executive/i.test(x))) return 'lead';
  if (s.some((x) => /senior/i.test(x))) return 'senior';
  if (s.some((x) => /mid/i.test(x))) return 'middle';
  if (s.some((x) => /entry|intern/i.test(x))) return 'junior';
  return null;
}

/** Примерный курс к доллару — только для сравнения с «зарплата от». */
const USD_RATE: Record<string, number> = {
  USD: 1,
  EUR: 1.08,
  GBP: 1.27,
  CHF: 1.13,
  CAD: 0.73,
  AUD: 0.66,
  NZD: 0.6,
  SGD: 0.74,
  PLN: 0.25,
  CZK: 0.043,
  SEK: 0.095,
  NOK: 0.093,
  DKK: 0.145,
  INR: 0.012,
  BRL: 0.18,
  MXN: 0.055,
  ZAR: 0.055,
  JPY: 0.0067,
  ILS: 0.27,
  AED: 0.27,
  UAH: 0.024,
};
const PER_MONTH: Record<string, number> = {
  annual: 1 / 12,
  monthly: 1,
  fortnightly: 26 / 12,
  weekly: 52 / 12,
  hourly: 160,
};
/** Сумма в валюте за период → $ в месяц (округлённо); неизвестная валюта или период — null. */
export function monthlyUsd(
  amount: number | null | undefined,
  currency: string | null,
  period: string | null,
) {
  if (!amount || !currency) return null;
  const rate = USD_RATE[currency.toUpperCase()];
  const k = PER_MONTH[period || 'annual'];
  return rate && k ? Math.round(amount * rate * k) : null;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Слово целиком: вокруг не буква и не цифра (работает и для кириллицы). */
const wordRe = (w: string) => new RegExp(`(?<![\\p{L}\\p{N}])${esc(w)}(?![\\p{L}\\p{N}])`, 'iu');

/** Бесплатный отбор по словам — до Claude: какие слова нашлись и есть ли исключения. */
export function keywordHit(text: string, f: Pick<FeedFilter, 'keywords' | 'exclude'>) {
  const hit = f.keywords.filter((w) => wordRe(w).test(text));
  const excluded = f.exclude.filter((w) => wordRe(w).test(text));
  return { pass: hit.length > 0 && excluded.length === 0, hit, excluded };
}

export interface FeedFacts {
  level: FeedLevel | null;
  format: Format | null;
  salaryMinUsd: number | null;
  salaryMaxUsd: number | null;
}

export const COUNTRY_RU: Record<string, string> = {
  ES: 'Испания',
  PT: 'Португалия',
  DE: 'Германия',
  FR: 'Франция',
  IT: 'Италия',
  PL: 'Польша',
  NL: 'Нидерланды',
  GB: 'Великобритания',
  UA: 'Украина',
};

/** Уровень, формат и зарплата против фильтра; неизвестное не отсекает (кроме зарплаты, если так задано). */
export function feedFilterFails(x: FeedFacts, f: FeedFilter): string | null {
  if (x.level && f.levels.length && !f.levels.includes(x.level)) return `уровень ${x.level}`;
  if (x.format && f.formats.length && !f.formats.includes(x.format)) return `формат ${x.format}`;
  if (f.salaryMinUsd) {
    const top = x.salaryMaxUsd ?? x.salaryMinUsd;
    if (top == null) return f.includeNoSalary ? null : 'зарплата не указана';
    if (top < f.salaryMinUsd) return `зарплата до $${top}`;
  }
  return null;
}

/* ---------- язык объявления ---------- */

/**
 * Частые служебные слова языков. Только те, что не совпадают с соседними языками: «de», «la», «que» — общие
 * для испанского, французского и португальского, поэтому есть лишь в испанском (их в вакансиях больше всего).
 */
const LANG_WORDS: Record<string, string[]> = {
  ru: 'и в во на с со для мы вы не что по от к у о об из это как или но наш наша наши ваш будет будете есть опыт работы работа компании компания вакансия требования условия задачи обязанности ищем мы ищем зарплата удаленно удалённо удаленка удалёнка знание года лет команда команде проекта проект продукт уровень плюсом'.split(
    ' ',
  ),
  uk: 'і й та з із зі у в на для ми ви не що від до як або але наш наша наші ваш буде є досвід роботи робота компанії компанія вакансія вимоги умови задачі обовʼязки обов’язки шукаємо зарплата віддалено знання років команда команді проєкту проєкт продукт рівень плюсом'.split(
    ' ',
  ),
  en: 'the and to of with for you we our your is are in on will be as an this that have has from at by it us or about join team experience work remote skills requirements responsibilities knowledge years engineer testing quality assurance automation'.split(
    ' ',
  ),
  es: 'de la el que y en los las del para con por una un se es su sus nuestro nuestra nuestros al lo como más experiencia equipo trabajo empresa buscamos ofrecemos requisitos años conocimientos ingeniero ingeniera pruebas calidad desarrollo remoto teletrabajo funciones también'.split(
    ' ',
  ),
  pt: 'em com não você nossa nosso equipe vaga são também conhecimento experiência trabalho remota'.split(
    ' ',
  ),
  de: 'und der die das mit für wir sie ist ein eine zu auf bei von nicht ihre erfahrung kenntnisse unser unsere arbeit'.split(
    ' ',
  ),
  fr: 'le les des et pour avec nous vous est dans sur du au expérience équipe poste vos notre'.split(' '),
  it: 'il di che gli della nel sono esperienza lavoro siamo cerchiamo nostro'.split(' '),
  pl: 'w z dla jest się oraz nie doświadczenie pracy zespół firma wymagania oferujemy'.split(' '),
};
const LANG_SETS = Object.entries(LANG_WORDS).map(([l, w]) => [l, new Set(w)] as const);

/**
 * Язык текста объявления по частым словам (и буквам «і ї є ґ» / «ы э ъ» для украинского и русского).
 * null — текст слишком короткий или смешанный («Senior QA Engineer»): такое фильтр не отсекает.
 */
export function detectLang(text: string): string | null {
  const words = (text ?? '')
    .slice(0, 6000)
    .replace(/https?:\/\/\S+|\S+@\S+|[@#]\w+/g, ' ')
    .toLowerCase()
    .match(/[\p{L}ʼ’]+/gu);
  if (!words) return null;
  const score: Record<string, number> = {};
  for (const w of words) {
    for (const [l, set] of LANG_SETS) if (set.has(w)) score[l] = (score[l] ?? 0) + 1;
    if (/[іїєґ]/.test(w)) score.uk = (score.uk ?? 0) + 1;
    else if (/[ыэъё]/.test(w)) score.ru = (score.ru ?? 0) + 1;
  }
  const [best, second] = Object.entries(score).sort((a, b) => b[1] - a[1]);
  if (!best || best[1] < 2 || (second && second[1] >= best[1])) return null;
  return best[0];
}

const LANG_RU: Record<string, string> = {
  ...FEED_LANG_LABELS,
  pt: 'Португальский',
  de: 'Немецкий',
  fr: 'Французский',
  it: 'Итальянский',
  pl: 'Польский',
};

/** Язык объявления не из выбранных — причина отсева («язык: испанский»); неизвестный язык проходит. */
export function feedLangFails(text: string, f: Pick<FeedFilter, 'languages'>): string | null {
  if (!f.languages.length) return null;
  const l = detectLang(text);
  if (!l || (f.languages as readonly string[]).includes(l)) return null;
  return `язык: ${(LANG_RU[l] ?? l).toLowerCase()}`;
}

/**
 * Основа роли без уточнений: «Lead QA Engineer (Backend / Platform)», «Lead QA Engineer — Backend»
 * и «Lead QA Engineer | remote» → «leadqaengineer». Разные каналы и Claude пишут роль то полно, то коротко.
 */
export function roleBase(role: string | null | undefined): string {
  const r = (role ?? '').split(/\s*[([|]|\s+[-–—/]\s+|,\s/)[0] ?? '';
  return normName(r) || normName(role);
}

/** Та же вакансия: та же компания и та же основа роли. */
export function sameVacancy(
  a: { company: string | null | undefined; role: string | null | undefined },
  b: { company: string | null | undefined; role: string | null | undefined },
): boolean {
  const c = normName(a.company);
  return !!c && c === normName(b.company) && roleBase(a.role) === roleBase(b.role);
}

/** Ключ склейки одинаковых вакансий из разных каналов; без компании и роли не склеиваем. */
export function feedDedupeKey(company: string, role: string): string | null {
  const c = normName(company);
  const r = roleBase(role);
  return c && r ? `${c}|${r}` : null;
}

const URL_RE = /https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"»]/gi;

/** Ссылки из текста по порядку. */
export function textLinks(text: string): string[] {
  return [...new Set(text.match(URL_RE) ?? [])];
}

/** Текст поста + ссылки, которых в тексте не видно (спрятаны в словах или кнопках). */
export function postWithLinks(text: string, links: readonly string[] = []): string {
  const extra = links.filter((u) => !text.includes(u));
  return extra.length ? `${text.trimEnd()}\n\n${extra.map((u) => `🔗 ${u}`).join('\n')}` : text;
}

/** Ссылка на описание вакансии в посте: первая не на сам Telegram. */
export function vacancyLink(text: string): string | null {
  return textLinks(text).find((u) => !/^https?:\/\/(?:www\.)?(?:t\.me|telegram\.me)\//i.test(u)) ?? null;
}

/* ---------- Claude: разбор постов ---------- */

type Json = Record<string, unknown>;
const str = (description?: string): Json =>
  description ? { type: 'string', description } : { type: 'string' };
const en = (values: readonly string[], description?: string): Json => ({
  type: 'string',
  enum: [...values],
  ...(description ? { description } : {}),
});
const numOrNull = (description: string): Json => ({ type: ['number', 'null'], description });

export const FEED_EXTRACT_TOOL = {
  name: 'save_vacancies',
  description: 'Сохранить разбор постов: по одному элементу на каждый пост.',
  input_schema: {
    type: 'object',
    properties: {
      items: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            i: { type: 'integer', description: 'номер поста из входа' },
            isVacancy: {
              type: 'boolean',
              description: 'true — пост с одной вакансией (или первой из нескольких)',
            },
            company: str('компания; пусто, если не указана'),
            role: str('должность как в посте'),
            level: en([...FEED_LEVELS, 'unknown']),
            format: en([...FORMATS, 'unknown'], 'remote — удалёнка, hybrid, office — офис'),
            location: str('город/страна или пусто'),
            salary: str('вилка как в посте или пусто'),
            salaryMinUsd: numOrNull('нижняя граница в $ в месяц (переведи из ₽, €, годовой) или null'),
            salaryMaxUsd: numOrNull('верхняя граница в $ в месяц или null'),
            fits: { type: 'boolean', description: 'подходит кандидату по фильтру и профилю' },
            reason: str('коротко по-русски: почему подходит или нет'),
          },
          required: [
            'i',
            'isVacancy',
            'company',
            'role',
            'level',
            'format',
            'location',
            'salary',
            'salaryMinUsd',
            'salaryMaxUsd',
            'fits',
            'reason',
          ],
          additionalProperties: false,
        },
      },
    },
    required: ['items'],
    additionalProperties: false,
  },
} as const;

export function feedExtractPrompt(o: {
  about: string;
  today: DateStr;
  filter: FeedFilter;
  posts: readonly { i: number; text: string }[];
}): string {
  const f = o.filter;
  const country = COUNTRY_RU[f.himalayasCountry] ?? f.himalayasCountry;
  return `Ты отбираешь вакансии из постов Telegram-каналов и объявлений с сайтов вакансий для кандидата: ${o.about || 'QA-инженер'}. Сегодня ${o.today}.
Фильтр кандидата: роли — ${f.keywords.join(', ')}; не подходят — ${f.exclude.join(', ') || '—'}; уровень — ${f.levels.join(', ') || 'любой'}; формат — ${f.formats.join(', ') || 'любой'}; зарплата — ${f.salaryMinUsd ? `от $${f.salaryMinUsd} в месяц` : 'любая'}.

Посты (i, text):
${JSON.stringify(o.posts.map((p) => ({ i: p.i, text: p.text.slice(0, 3000) })))}

Вызови инструмент ${FEED_EXTRACT_TOOL.name}, по элементу на каждый пост:
- isVacancy — false для рекламы, курсов, резюме соискателей, новостей, подборок без конкретной вакансии;
- fits — true, только если это вакансия под роль и уровень кандидата и не противоречит фильтру; неизвестное (формат, зарплата) не причина отказа;
- level, format — unknown, если в посте не сказано; поля, которых нет в посте, оставь пустыми — не выдумывай;${
    country
      ? `\n- кандидат живёт в стране: ${country}; удалёнка только для жителей другой страны («only Ukraine», «тільки для резидентів України», «US only», «LATAM only») — fits=false.`
      : ''
  }`;
}

/* ---------- Djinni ---------- */

/** RSS Djinni: QA (ручное и автоматизация), полная удалёнка, опыт по уровням фильтра. */
export function djinniRssUrl(levels: readonly FeedLevel[]): string {
  const p = new URLSearchParams();
  for (const k of ['QA', 'QA Automation']) p.append('primary_keyword', k);
  p.set('remote_type', 'full_remote');
  const exp = new Set<string>();
  for (const l of levels.length ? levels : FEED_LEVELS) {
    if (l === 'junior') ['no_exp', '1y'].forEach((x) => exp.add(x));
    if (l === 'middle') ['2y', '3y'].forEach((x) => exp.add(x));
    if (l === 'senior' || l === 'lead') exp.add('5y');
  }
  for (const x of exp) p.append('exp_level', x);
  return `https://djinni.co/jobs/rss/?${p}`;
}

export interface DjinniItem {
  /** Номер вакансии из ссылки /jobs/<id>-…/ */
  id: string;
  title: string;
  url: string;
  date: Date;
  /** Описание (HTML). */
  html: string;
}

const tagOf = (item: string, tag: string) =>
  (item.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`))?.[1] ?? '')
    .replace(/^\s*<!\[CDATA\[|\]\]>\s*$/g, '')
    .trim();

/** Вакансии из RSS Djinni. */
export function parseDjinniRss(xml: string): DjinniItem[] {
  const out: DjinniItem[] = [];
  for (const m of xml.matchAll(/<item>([\s\S]*?)<\/item>/g)) {
    const it = m[1]!;
    const url = tagOf(it, 'link').replace(/&amp;/g, '&');
    const id = url.match(/\/jobs\/(\d+)/)?.[1];
    if (!id) continue;
    const date = new Date(tagOf(it, 'pubDate'));
    out.push({
      id,
      title: tagOf(it, 'title')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'"),
      url: url.split('?')[0]!,
      date: Number.isNaN(date.getTime()) ? new Date() : date,
      html: tagOf(it, 'description'),
    });
  }
  return out;
}
